import { CaseType, MatrixType, OrderType, ProductGender } from '@onrunning/backstage-models';

import type { MatrixProduct } from '@/models/matrix-product.model';
import { useCaseStore } from '@/stores/case';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import type { MatrixOrderProduct } from '@/types/product.interface';
import { computed } from 'vue';

export function useMatrixProduct() {
  const productMatrixStore = useProductMatrixStore();
  const orderStore = useOrderStore();
  const caseStore = useCaseStore();

  const isNewOrder = computed(() => !productMatrixStore.activeMatrixOrder.orderNumber);

  const matrixType = computed<MatrixType>(() => {
    if (orderStore.orderType) {
      switch (orderStore.orderType) {
        case OrderType.LastSeason:
          return MatrixType.lastSeason;
        case OrderType.ReOrder:
        case OrderType.DropShip:
          return MatrixType.order;
        case OrderType.SpecialProjects:
          return MatrixType.specialProjects;
        default:
          return MatrixType.preorder;
      }
    } else if (caseStore.caseType) {
      return caseStore.caseType === CaseType.Warranty ? MatrixType.warranty : MatrixType.return;
    }

    return MatrixType.preorder;
  });

  /**
   * Handles quantity change for a single matrix product.
   */
  function onProductQuantityChange(product: MatrixProduct, quantity: number) {
    const currentMatrixOrderTotal = productMatrixStore.activeMatrixOrder.total;
    const products = { ...productMatrixStore.activeMatrixOrder.matrixProducts };

    const existingProduct = products[product.productId];

    // No change, skip update
    if (existingProduct && existingProduct.quantity === quantity) return;

    // Remove product entirely if quantity is 0 and it never existed on the backend order/draft -
    // either it's a new order/draft, or the line was added during this edit session. Only lines
    // that existed on the backend must be kept at quantity 0, to signal removal to the backend.
    if (existingProduct && quantity === 0 && (isNewOrder.value || existingProduct.addedDuringEdit)) {
      delete products[product.productId];

      productMatrixStore.updateMatrixOrder(
        { matrixProducts: products, total: currentMatrixOrderTotal - existingProduct.quantity },
        productMatrixStore.activeMatrixOrderIndex,
      );

      return;
    }

    // Update product quantity if product exists
    if (existingProduct) {
      products[product.productId] = { ...product, quantity, addedDuringEdit: existingProduct.addedDuringEdit };

      productMatrixStore.updateMatrixOrder(
        { matrixProducts: products, total: currentMatrixOrderTotal + quantity - existingProduct.quantity },
        productMatrixStore.activeMatrixOrderIndex,
      );

      return;
    }

    // Add product if not present and quantity > 0. Flag lines added while editing an existing
    // order/draft so a later reduction back to 0 drops them instead of signalling a removal for
    // a line the backend never had.
    if (!existingProduct && quantity > 0) {
      products[product.productId] = { ...product, quantity, addedDuringEdit: !isNewOrder.value };

      productMatrixStore.updateMatrixOrder(
        { matrixProducts: products, total: currentMatrixOrderTotal + quantity },
        productMatrixStore.activeMatrixOrderIndex,
      );
    }
  }

  /**
   * Removes quantities for a group of products by gender and group name.
   */
  function onRemoveProductGroupQuantities(gender: ProductGender, productGroupName: string) {
    let matrixTotal = productMatrixStore.activeMatrixOrder.total;

    const matrixProducts = { ...productMatrixStore.activeMatrixOrder.matrixProducts };
    const updatedProducts: Record<MatrixProduct['productId'], MatrixOrderProduct> = {};

    for (const productId in matrixProducts) {
      const matrixProduct = matrixProducts[productId];
      const product = productMatrixStore.products[productId];
      const isProduct = !!product && product.gender === gender && product.productGroupName === productGroupName;

      if (isProduct) {
        matrixTotal -= matrixProduct.quantity;

        // Keep the line at quantity 0 only if it existed on the backend order/draft, so the
        // update payload signals removal. Lines added during this edit session are simply
        // dropped, since the backend never had them.
        if (!isNewOrder.value && !matrixProduct.addedDuringEdit) {
          updatedProducts[productId] = { ...matrixProduct, quantity: 0 };
        }
      } else {
        updatedProducts[productId] = matrixProduct;
      }
    }

    productMatrixStore.updateMatrixOrder(
      { matrixProducts: updatedProducts, total: matrixTotal },
      productMatrixStore.activeMatrixOrderIndex,
    );
  }

  function updateMatrixTotal(total: number) {
    productMatrixStore.setMatrixOrderTotal(total, productMatrixStore.activeMatrixOrderIndex);
  }

  return {
    updateMatrixTotal,
    onProductQuantityChange,
    onRemoveProductGroupQuantities,
    matrixType,
  };
}
